import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { resetPassword } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Shield, CheckCircle2, AlertCircle } from 'lucide-react';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await resetPassword(token, password);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Reset link is invalid or expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-[#09090B]">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <Shield className="w-8 h-8 text-[#C0A062]" />
          <span className="text-xl font-bold text-white">
            DOJO<span className="text-[#C0A062]">DASH</span>
          </span>
        </Link>

        {!token ? (
          <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-6" data-testid="reset-invalid-link">
            <AlertCircle className="w-8 h-8 text-amber-400 mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Invalid reset link</h1>
            <Link to="/forgot-password" className="inline-block mt-4 text-sm text-[#C0A062] hover:underline" data-testid="reset-request-new-link">
              Request a new one →
            </Link>
          </div>
        ) : success ? (
          <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-6" data-testid="reset-success-message">
            <CheckCircle2 className="w-8 h-8 text-emerald-400 mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Password updated.</h1>
            <p className="text-zinc-400">Taking you to login...</p>
          </div>
        ) : (
          <>
            <h1 className="text-3xl font-bold text-white mb-2">Set a new password</h1>
            <p className="text-zinc-400 mb-8">Choose a strong password of at least 8 characters.</p>

            {error && (
              <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-center gap-3" data-testid="reset-error-message">
                <AlertCircle className="w-5 h-5 text-amber-400 shrink-0" />
                <span className="text-amber-300 text-sm">
                  {error}{' '}
                  {error.toLowerCase().includes('invalid') || error.toLowerCase().includes('expired') ? (
                    <Link to="/forgot-password" className="text-[#C0A062] hover:underline" data-testid="reset-request-new-link">
                      Request a new one →
                    </Link>
                  ) : null}
                </span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6" data-testid="reset-password-form">
              <div>
                <Label htmlFor="new-password" className="text-zinc-300">New password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-2 bg-[#18181B] border-zinc-800 text-white"
                  placeholder="••••••••"
                  required
                  data-testid="reset-new-password"
                />
              </div>
              <div>
                <Label htmlFor="confirm-password" className="text-zinc-300">Confirm password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="mt-2 bg-[#18181B] border-zinc-800 text-white"
                  placeholder="••••••••"
                  required
                  data-testid="reset-confirm-password"
                />
              </div>
              <Button type="submit" disabled={loading} className="w-full btn-primary py-6" data-testid="reset-submit-btn">
                {loading ? <span className="spinner w-5 h-5" /> : 'Update password'}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
